import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { ImageOff, ShieldX } from "lucide-react";
import { cn } from "@/lib/utils";

export type ImageErrorType = "access_denied" | "not_found" | "unknown";

interface AuthenticatedImageProps {
  /** The proxy URL to fetch the image from */
  src: string;
  /** Alt text for the image */
  alt: string;
  /** Optional className for styling */
  className?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Optional error callback with error type */
  onError?: (errorType: ImageErrorType) => void;
  /** Optional load success callback */
  onLoad?: () => void;
  /** Show skeleton while loading (default: true) */
  showSkeleton?: boolean;
  /** Show inline error UI (default: true). Set to false to handle error externally */
  showErrorUI?: boolean;
  /** Cache buster value - change to force refetch (e.g., timestamp or version) */
  cacheBuster?: string | number;
}

/**
 * In-memory cache for authenticated images.
 * Maps proxy URLs to object URLs (blobs).
 * Persists for the session lifetime.
 */
const imageCache = new Map<string, string>();

/**
 * Maximum cache size (number of images).
 * Prevents unbounded memory growth.
 */
const MAX_CACHE_SIZE = 100;

/**
 * Clears oldest entries when cache exceeds max size.
 */
function pruneCache(): void {
  if (imageCache.size > MAX_CACHE_SIZE) {
    const entriesToRemove = imageCache.size - MAX_CACHE_SIZE;
    const keys = Array.from(imageCache.keys());

    for (let i = 0; i < entriesToRemove; i++) {
      const key = keys[i];
      const objectUrl = imageCache.get(key);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      imageCache.delete(key);
    }
  }
}

/**
 * Image component that fetches images with authentication headers.
 * Used for private storage images accessed via the get-storage-image Edge Function.
 * Converts the response blob to an Object URL for display.
 *
 * Features in-memory caching to prevent re-fetching already loaded images.
 */
export function AuthenticatedImage({
  src,
  alt,
  className,
  onClick,
  onError,
  onLoad,
  showSkeleton = true,
  showErrorUI = true,
  cacheBuster,
}: AuthenticatedImageProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<ImageErrorType | null>(null);
  const [loading, setLoading] = useState(true);

  // Create a cache key that includes the cacheBuster to force refetch when it changes
  const cacheKey = cacheBuster ? `${src}?_cb=${cacheBuster}` : src;

  useEffect(() => {
    let cancelled = false;

    async function loadImage() {
      // Data URLs can be used directly (don't cache)
      if (src.startsWith("data:")) {
        setImageSrc(src);
        setLoading(false);
        onLoad?.();
        return;
      }

      // Check cache first (using cacheKey which includes cacheBuster)
      const cached = imageCache.get(cacheKey);
      if (cached) {
        setImageSrc(cached);
        setLoading(false);
        setErrorType(null);
        onLoad?.();
        return;
      }

      try {
        // Get current session for auth header
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const response = await fetch(src, {
          headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
        });

        if (cancelled) return;

        if (!response.ok) {
          // Determine error type based on status code
          let errType: ImageErrorType = "unknown";
          if (response.status === 403) {
            errType = "access_denied";
          } else if (response.status === 404) {
            errType = "not_found";
          }

          if (cancelled) return;
          setErrorType(errType);
          onError?.(errType);
          return;
        }

        const blob = await response.blob();
        if (cancelled) return;

        const objectUrl = URL.createObjectURL(blob);

        // Store in cache (using cacheKey which includes cacheBuster)
        imageCache.set(cacheKey, objectUrl);
        pruneCache();

        setImageSrc(objectUrl);
        setErrorType(null);
        onLoad?.();
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load authenticated image:", err);
        setErrorType("unknown");
        onError?.("unknown");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    setLoading(true);
    setErrorType(null);
    setImageSrc(null);
    loadImage();

    return () => {
      cancelled = true;
      // Don't revoke cached URLs - they persist for session lifetime
    };
  }, [src, cacheKey, onError, onLoad]);

  // Expose error type for external handling
  const hasError = errorType !== null;

  if (loading && showSkeleton) {
    return <Skeleton className={className} />;
  }

  if (hasError || !imageSrc) {
    // If showErrorUI is false, render nothing (parent handles error)
    if (!showErrorUI) {
      return null;
    }

    // Show access denied icon for 403 errors
    const Icon = errorType === "access_denied" ? ShieldX : ImageOff;

    return (
      <div className={cn("flex items-center justify-center bg-muted", className)}>
        <Icon className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  return <img src={imageSrc} alt={alt} className={className} onClick={onClick} />;
}

/**
 * Utility to clear the image cache (useful for testing or logout).
 */
export function clearImageCache(): void {
  imageCache.forEach((objectUrl) => {
    URL.revokeObjectURL(objectUrl);
  });
  imageCache.clear();
}
